import React from "react";
import { Link } from "react-router-dom";
import {
  Github,
  Bug,
  Mail,
  Heart,
  ShieldCheck,
  Lock,
  MapPin,
} from "lucide-react";

const NAV_LINKS = [
  { path: "/stories", label: "Stories" },
  { path: "/supportUkraine", label: "Art for Ukraine" },
  { path: "/blogs", label: "Blog" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Connect" },
  { path: "/faq", label: "FAQ" },
];

const LEGAL_LINKS = [
  { path: "/privacy", label: "Privacy Policy", external: false },
  { path: "/terms", label: "Terms", external: false },
  { path: "/sitemap.xml", label: "Sitemap", external: true },
];

const SOCIAL_LINKS = [
  { href: "https://github.com/anettta", label: "GitHub", icon: Github },
  { href: "mailto:sanguineaccent@gmail.com", label: "Email", icon: Mail },
  {
    href: "https://docs.google.com/forms/d/e/1FAIpQLSdPuVphhzDgCgRA68csoejkaM9Xr0QMzjc24poPuH5TDrLqgg/viewform?usp=publish-editor",
    label: "Report a Bug",
    icon: Bug,
  },
];

const SECURE_BADGES = [
  { icon: ShieldCheck, label: "Secure checkout" },
  { icon: Lock, label: "SSL encrypted" },
  { icon: ShieldCheck, label: "Card details never stored" },
];

const PAYMENT_METHODS = [
  {
    id: "visa",
    label: "Visa",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#fff" />
        <text
          x="6"
          y="22"
          fontFamily="Arial"
          fontWeight="900"
          fontSize="17"
          fill="#1A1F71"
          letterSpacing="-0.5"
        >
          VISA
        </text>
      </svg>
    ),
  },
  {
    id: "mastercard",
    label: "Mastercard",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#fff" />
        <circle cx="19" cy="16" r="9" fill="#EB001B" />
        <circle cx="31" cy="16" r="9" fill="#F79E1B" />
        <path d="M25 8.6a9 9 0 0 1 0 14.8A9 9 0 0 1 25 8.6z" fill="#FF5F00" />
      </svg>
    ),
  },
  {
    id: "amex",
    label: "American Express",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#2557D6" />
        <text
          x="5"
          y="21"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="11"
          fill="#fff"
          letterSpacing="0.5"
        >
          AMEX
        </text>
      </svg>
    ),
  },
  {
    id: "applepay",
    label: "Apple Pay",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#000" />

        <path
          d="M21.5 11.2c.7-.9.6-1.8.6-1.8s-.9.1-1.6.8c-.6.6-.8 1.5-.7 1.5s.9 0 1.7-.5zm-1.8 1c-1 0-1.8.6-2.3.6-.6 0-1.3-.6-2.2-.5-1.1 0-2.1.6-2.6 1.6-1.1 1.9-.3 4.8.8 6.4.5.8 1.2 1.6 2 1.5.8 0 1.1-.5 2-.5 1 0 1.2.5 2 .5.9 0 1.5-.8 2-1.5.4-.6.7-1.2.8-1.9-2-.8-1.7-3.4.3-4.1-.6-.9-1.7-1.5-2.8-1.6z"
          fill="#fff"
        />
        <text
          x="27"
          y="21"
          fontFamily="-apple-system, Arial"
          fontWeight="600"
          fontSize="10"
          fill="#fff"
        >
          Pay
        </text>
      </svg>
    ),
  },
  {
    id: "googlepay",
    label: "Google Pay",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect
          width="50"
          height="32"
          rx="4"
          fill="#fff"
          stroke="#e0e0e0"
          strokeWidth="0.5"
        />

        <text
          x="5"
          y="21"
          fontFamily="Arial"
          fontWeight="700"
          fontSize="11"
          fill="#4285F4"
        >
          G
        </text>
        <text
          x="13"
          y="21"
          fontFamily="Arial"
          fontWeight="500"
          fontSize="11"
          fill="#3C4043"
        >
          Pay
        </text>
      </svg>
    ),
  },
  {
    id: "cashapp",
    label: "Cash App Pay",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#00D64F" />
        <text
          x="8"
          y="21"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="10"
          fill="#fff"
        >
          Cash
        </text>
        <text
          x="8"
          y="29"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="7"
          fill="#fff"
        >
          App
        </text>
        <text
          x="29"
          y="22"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="16"
          fill="#fff"
        >
          $
        </text>
      </svg>
    ),
  },
  {
    id: "link",
    label: "Link by Stripe",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#00D66B" />
        <text
          x="8"
          y="22"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="13"
          fill="#fff"
        >
          Link
        </text>
      </svg>
    ),
  },
  {
    id: "klarna",
    label: "Klarna",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#FFB3C7" />
        <text
          x="6"
          y="22"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="12"
          fill="#0A0A0A"
        >
          klarna
        </text>
      </svg>
    ),
  },
  {
    id: "afterpay",
    label: "Afterpay",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#B2FCE4" />
        <text
          x="4"
          y="14"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="8"
          fill="#000"
        >
          after
        </text>
        <text
          x="4"
          y="25"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="8"
          fill="#000"
        >
          pay
        </text>

        <circle cx="38" cy="16" r="8" fill="#000" />
        <text
          x="34.5"
          y="20"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="11"
          fill="#B2FCE4"
        >
          A
        </text>
      </svg>
    ),
  },
  {
    id: "affirm",
    label: "Affirm",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect
          width="50"
          height="32"
          rx="4"
          fill="#fff"
          stroke="#e0e0e0"
          strokeWidth="0.5"
        />
        <text
          x="5"
          y="22"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="13"
          fill="#0FA0EA"
        >
          affirm
        </text>
      </svg>
    ),
  },
  {
    id: "ideal",
    label: "iDEAL",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect
          width="50"
          height="32"
          rx="4"
          fill="#fff"
          stroke="#e0e0e0"
          strokeWidth="0.5"
        />
        <text
          x="5"
          y="14"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="9"
          fill="#CC0066"
        >
          iDEAL
        </text>
        <text x="5" y="25" fontFamily="Arial" fontSize="7" fill="#333">
          /Wero
        </text>
      </svg>
    ),
  },
  {
    id: "bancontact",
    label: "Bancontact",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#005498" />
        <text
          x="4"
          y="15"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="7.5"
          fill="#fff"
        >
          ban
        </text>
        <text
          x="4"
          y="25"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="7.5"
          fill="#fff"
        >
          contact
        </text>

        <rect x="33" y="7" width="12" height="8" rx="1" fill="#fff" />
        <rect x="33" y="17" width="12" height="8" rx="1" fill="#F4A700" />
      </svg>
    ),
  },
  {
    id: "eps",
    label: "EPS",
    svg: (
      <svg viewBox="0 0 50 32" width="50" height="32" aria-hidden="true">
        <rect width="50" height="32" rx="4" fill="#CC0000" />
        <text
          x="10"
          y="22"
          fontFamily="Arial"
          fontWeight="bold"
          fontSize="15"
          fill="#fff"
        >
          EPS
        </text>
      </svg>
    ),
  },
];

const PaymentIcons = () => (
  <div
    className="flex flex-wrap gap-1 justify-center "
    role="list"
    aria-label="Accepted payment methods"
  >
    {PAYMENT_METHODS.map(({ id, label, svg }) => (
      <span
        key={id}
        role="listitem"
        title={label}
        aria-label={label}
        className="inline-flex items-center justify-center rounded-[4px] border border-stone-200 bg-white overflow-hidden shadow-sm"
        style={{ width: 30, height: 19 }}
      >
        {svg}
      </span>
    ))}
  </div>
);

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#f5f3ea] border-t border-[#e2e1d8] font-sourcesans text-auto text-[#1c1917]">
      <div className="pb-20 md:pb-0">
        <div className="border-b border-[#e2e1d8] py-8 px-4 sm:px-6">
          <div className="max-w-xl mx-auto text-center space-y-3">
            <p
              className="font-playfair text-lg sm:text-xl md:text-2xl text-stone-800 tracking-wide leading-snug"
              style={{ textWrap: "balance" }}
            >
              Creating beauty with honesty and purpose.
            </p>

            <p
              className="text-sm sm:text-base text-stone-500 leading-relaxed"
              style={{ textWrap: "balance" }}
            >
              Handmade cards. Honest writing. Real impact.
            </p>

            <p
              className="text-xs sm:text-sm text-stone-400 leading-relaxed flex items-center justify-center gap-1.5 flex-wrap"
              style={{ textWrap: "balance" }}
            >
              70% of every purchase goes directly to people serving in Ukraine
              <Heart
                className="w-3 h-3 text-[#BC3F4A] shrink-0"
                aria-hidden="true"
              />
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1 flex flex-col gap-4">
            <div>
              <h2 className="font-playfair font-semibold text-base text-[#BC3F4A] uppercase tracking-tight">
                Sanguine Accent
              </h2>
              <p className="text-[10px] uppercase tracking-widest text-stone-400 mt-0.5">
                Make it. Mean it.
              </p>
            </div>
            <p className="text-stone-600 text-xs leading-relaxed">
              Each card is made by hand. 70% goes straight to people I trust in
              Ukraine.
            </p>
            <p className="flex items-center gap-1.5 text-xs text-stone-500">
              <MapPin className="w-3 h-3 shrink-0" aria-hidden="true" />
              New York
            </p>
          </div>

          {/* Explore */}
          <nav aria-label="Footer navigation">
            <h3 className="font-playfair font-semibold text-xs uppercase tracking-widest text-[#BC3F4A] mb-4">
              Explore
            </h3>
            <ul className="flex flex-col gap-2.5">
              {NAV_LINKS.map(({ path, label }) => (
                <li key={path}>
                  <Link
                    to={path}
                    className="text-stone-600 hover:text-[#BC3F4A] transition-colors duration-200 text-xs"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="font-playfair font-semibold text-xs uppercase tracking-widest text-[#BC3F4A] mb-4">
              Connect
            </h3>
            <ul className="flex flex-col gap-3">
              {SOCIAL_LINKS.map(({ href, label, icon: Icon }) => (
                <li key={href}>
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={
                      href.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="inline-flex items-center gap-2 text-stone-600 hover:text-[#BC3F4A] transition-colors duration-200 text-xs"
                  >
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-playfair font-semibold text-xs uppercase tracking-widest text-[#BC3F4A] mb-4">
              Support
            </h3>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to="/faq"
                  className="text-stone-600 hover:text-[#BC3F4A] transition-colors duration-200 text-xs"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-stone-600 hover:text-[#BC3F4A] transition-colors duration-200 text-xs"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/me/orders"
                  className="text-stone-600 hover:text-[#BC3F4A] transition-colors duration-200 text-xs"
                >
                  My Orders
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#e2e1d8] px-6 py-5">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-[10px] uppercase tracking-widest text-stone-400">
                Accepted payment methods
              </p>
              <PaymentIcons />
            </div>

            <div className="flex flex-wrap items-center gap-1 md:gap-4 justify-center md:justify-end">
              {SECURE_BADGES.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 text-[11px] text-stone-400"
                >
                  <Icon
                    className="w-3.5 h-3.5 text-[#3fbcb1] shrink-0"
                    aria-hidden="true"
                  />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-[#e2e1d8] py-4 px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-stone-400">
            <p>© {year} Sanguine Accent · by Anna Gapyuk</p>
            <ul className="flex items-center gap-4">
              {LEGAL_LINKS.map(({ path, label, external }) => (
                <li key={path}>
                  {external ? (
                    <a
                      href={path}
                      className="hover:text-[#BC3F4A] transition-colors duration-200"
                    >
                      {label}
                    </a>
                  ) : (
                    <Link
                      to={path}
                      className="hover:text-[#BC3F4A] transition-colors duration-200"
                    >
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
